import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { vars } from '@/lib/motion'

const LETTERS = 'KERNING'.split('')

/* Collapse: the letters slide into the K, far end first, then the K hands over to the mark. */
const COLLAPSE_DELAY = [170, 100, 80, 60, 40, 20, 0]

/* Expand: the mark gives way, the K returns, and the word spells itself back out from the K. */
const EXPAND_DELAY = [60, 110, 132, 154, 176, 198, 220]

interface LogoMarkProps {
    className?: string
}

/** The K mark: a bold, slightly bracketed K sized to the wordmark's cap height. */
export const LogoMark: FC<LogoMarkProps> = ({ className }): ReactNode => {
    return (
        <svg viewBox='0 0 20 20' aria-hidden='true' className={cn('h-5 w-5', className)}>
            <path fill='currentColor' d='M1.8 1.8h7.1v1.1l-1.6.3v13.6l1.6.3v1.1H1.8v-1.1l1.5-.3V3.2l-1.5-.3z' />
            <path fill='currentColor' d='M7.3 10.9 14.6 3.3l-1.4-.4V1.8h5.6v1.1l-1.3.3-5.7 5.6 5.9 8.8 1.4.4v1.2h-6.3v-1.1l1.1-.3-4.2-6.6-1.7 1.6z' />
        </svg>
    )
}

interface LogoProps {
    /** When true the letters slide into the K, last letter first, and the K mark takes its place. */
    collapsed?: boolean
    className?: string
}

/** The KERNING wordmark in the book serif, widely spaced. */
export const Logo: FC<LogoProps> = ({ collapsed = false, className }): ReactNode => {
    return (
        <span className={cn('relative inline-flex h-6 items-center text-ink', className)}>
            <span aria-hidden='true' className='inline-flex items-baseline whitespace-pre'>
                {LETTERS.map((letter, index) => (
                    <i
                        key={index}
                        className='font-serif text-[26px] not-italic leading-none tracking-[0.14em] transition-[opacity,transform] duration-[240ms,340ms] ease-[ease,cubic-bezier(0.22,1,0.36,1)]'
                        style={{
                            ...vars({ '--li': index }),
                            opacity: collapsed ? 0 : 1,
                            transform: collapsed && index > 0 ? 'translateX(calc(var(--li) * -0.46em))' : 'none',
                            transitionDelay: `${collapsed ? COLLAPSE_DELAY[index] : EXPAND_DELAY[index]}ms`
                        }}
                    >
                        {letter}
                    </i>
                ))}
            </span>

            <span
                aria-hidden='true'
                className='absolute left-0 top-1/2 origin-left transition-[opacity,transform] duration-[220ms,300ms] ease-[ease,cubic-bezier(0.34,1.36,0.64,1)]'
                style={{
                    opacity: collapsed ? 1 : 0,
                    transform: `translateY(-50%) scale(${collapsed ? 1 : 0.82})`,
                    transitionDelay: collapsed ? '210ms' : '0ms'
                }}
            >
                <LogoMark />
            </span>
        </span>
    )
}
